import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PaymentIcon from '@mui/icons-material/Payment';
import { formatPaymentTime } from '../../utils/dateUtils';
import { formatBookingCurrency } from '../../utils/bookingPaymentSummary';
import { BOOKING_DRAWER_ACCENT } from '../../utils/bookingDrawerLayout';

/**
 * Collapsible payment-only history for payment drawers.
 * Extra-charge changes belong in booking audit, not here.
 */
export default function BookingFinancialHistoryPanel({
  payments = [],
  currency = 'INR',
  hotelTimezone,
  isDark = false,
  drawerTheme = {},
  accent = BOOKING_DRAWER_ACCENT,
  title = 'Payment History',
  defaultExpanded = true,
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const entries = useMemo(() => {
    const list = Array.isArray(payments) ? payments : [];
    return [...list].sort((a, b) => {
      const ta = new Date(a?.processed_at || a?.payment_date || a?.created_at || 0).getTime();
      const tb = new Date(b?.processed_at || b?.payment_date || b?.created_at || 0).getTime();
      return (Number.isFinite(tb) ? tb : 0) - (Number.isFinite(ta) ? ta : 0);
    });
  }, [payments]);

  const subtle = drawerTheme.subtleText || (isDark ? '#999' : '#666');
  const text = drawerTheme.text || (isDark ? '#ddd' : '#333');
  const surface = drawerTheme.surface || (isDark ? '#1e1e1e' : '#fff');

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        mb: 2,
        backgroundColor: surface,
        border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
        borderRadius: '12px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          mb: expanded ? 1.5 : 0,
        }}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <Typography component="div" variant="subtitle2" sx={{ color: text, fontWeight: 600 }}>
          {title}
          {entries.length > 0 ? (
            <Chip
              label={entries.length}
              size="small"
              sx={{
                ml: 1,
                height: 20,
                fontSize: '0.65rem',
                backgroundColor: isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)',
                color: accent,
                fontWeight: 600,
              }}
            />
          ) : null}
        </Typography>
        <IconButton
          size="small"
          sx={{
            color: subtle,
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s',
          }}
        >
          <ExpandMoreIcon />
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        {entries.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3, px: 2 }}>
            <PaymentIcon sx={{ fontSize: 48, color: subtle, mb: 1, opacity: 0.5 }} />
            <Typography variant="body2" sx={{ color: subtle, textAlign: 'center', fontSize: '0.85rem', fontWeight: 500 }}>
              No payment history found
            </Typography>
            <Typography variant="caption" sx={{ color: isDark ? '#777' : '#999', textAlign: 'center', fontSize: '0.75rem', mt: 0.5 }}>
              Payments will appear here once collected
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {entries.map((payment, index) => {
              const paymentDate = payment.processed_at || payment.payment_date || payment.created_at;
              return (
                <Paper
                  key={payment.id || index}
                  elevation={0}
                  sx={{
                    p: 1.25,
                    pb: 1,
                    borderRadius: '8px',
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                    border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)'}`,
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5, gap: 1 }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 700,
                          color: isDark ? '#fff' : '#212121',
                          fontSize: '0.95rem',
                        }}
                      >
                        {formatBookingCurrency(payment.amount || 0, payment.currency || currency)}
                      </Typography>
                      {paymentDate ? (
                        <Typography variant="caption" sx={{ color: subtle, fontSize: '0.7rem' }}>
                          {formatPaymentTime(paymentDate, hotelTimezone)}
                        </Typography>
                      ) : null}
                    </Box>
                    {payment.payment_method ? (
                      <Chip
                        label={payment.payment_method}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          backgroundColor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)',
                          color: accent,
                          fontWeight: 600,
                          flexShrink: 0,
                        }}
                      />
                    ) : null}
                  </Box>
                  {payment.reference_number ? (
                    <Typography variant="caption" sx={{ display: 'block', color: subtle, fontSize: '0.7rem', mt: 0.35 }}>
                      Ref: {payment.reference_number}
                    </Typography>
                  ) : null}
                </Paper>
              );
            })}
          </Box>
        )}
      </Collapse>
    </Paper>
  );
}
